import React, { useState } from "react";
import { Send, Mail, MessageCircle, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { MarketingShell } from "@/components/marketing/MarketingShell";
import { BrutalButton, BentoCard } from "@/components/dashboard/BentoCard";

export const CONTACT_TESTIDS = {
  root: "contact-page",
  name: "contact-name",
  email: "contact-email",
  message: "contact-message",
  honeypot: "contact-honeypot",
  submit: "contact-submit",
  success: "contact-success",
};

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "", honeypot: "" });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const disabled =
    submitting || !form.name.trim() || !form.email.trim() || form.message.trim().length < 5;

  const submit = async (e) => {
    e.preventDefault();
    if (disabled) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/contact", form);
      setSent(true);
      if (data.delivery === "sent") {
        toast.success("Message sent — check your inbox for a reply soon.");
      } else {
        toast("Message received. We'll respond as soon as email delivery is live.", { icon: "📮" });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Send failed. Try again in a minute.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MarketingShell>
      <section
        data-testid={CONTACT_TESTIDS.root}
        className="max-w-4xl mx-auto px-6 pt-16 pb-24 grid md:grid-cols-5 gap-10"
      >
        <div className="md:col-span-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 border-2 border-black bg-yellow-300 font-mono-data text-[10px] uppercase tracking-widest">
            <MessageCircle size={12} /> Contact
          </div>
          <h1 className="mt-6 font-display text-4xl md:text-5xl font-bold tracking-tight leading-[1.05]">
            Talk to a human.
          </h1>
          <p className="mt-4 text-zinc-700">
            Feature requests, county-adapter requests, refund questions,
            press — anything. Direct line, no gatekeeping.
          </p>
          <div className="mt-6 space-y-3 text-sm">
            <div className="flex items-center gap-3">
              <Mail size={16} className="text-red-600" />
              <a href="mailto:sbrown@lienscoutapp.com" className="font-mono-data hover:underline">
                sbrown@lienscoutapp.com
              </a>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle size={16} className="text-emerald-600 mt-0.5" />
              <div>
                <div className="font-display font-semibold">24-hr response</div>
                <div className="text-xs text-zinc-500">
                  Every message read personally, most answered same day.
                </div>
              </div>
            </div>
          </div>
        </div>

        <BentoCard className="md:col-span-3 !p-6">
          {sent ? (
            <div data-testid={CONTACT_TESTIDS.success} className="text-center py-10">
              <CheckCircle size={44} className="mx-auto text-emerald-500" strokeWidth={1.75} />
              <div className="mt-4 font-display font-bold text-2xl">Got it.</div>
              <p className="mt-2 text-sm text-zinc-600">
                Your message landed. Expect a reply from{" "}
                <span className="font-mono-data">sbrown@lienscoutapp.com</span> within
                a business day.
              </p>
              <button
                onClick={() => { setSent(false); setForm({ name: "", email: "", message: "", honeypot: "" }); }}
                className="mt-6 font-display text-sm text-red-600 underline decoration-yellow-500 decoration-2 underline-offset-4"
              >
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="data-label block mb-1.5">Name</label>
                <input
                  data-testid={CONTACT_TESTIDS.name}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 border-2 border-zinc-200 focus:border-yellow-500 focus:outline-none bg-white text-sm"
                  maxLength={120}
                  required
                />
              </div>
              <div>
                <label className="data-label block mb-1.5">Email</label>
                <input
                  data-testid={CONTACT_TESTIDS.email}
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2 border-2 border-zinc-200 focus:border-yellow-500 focus:outline-none bg-white text-sm"
                  required
                />
              </div>
              <div>
                <label className="data-label block mb-1.5">Message</label>
                <textarea
                  data-testid={CONTACT_TESTIDS.message}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full px-3 py-2 border-2 border-zinc-200 focus:border-yellow-500 focus:outline-none bg-white text-sm min-h-[140px]"
                  maxLength={4000}
                  required
                  minLength={5}
                />
              </div>
              {/* Honeypot — hidden from humans, filled by bots */}
              <input
                data-testid={CONTACT_TESTIDS.honeypot}
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.honeypot}
                onChange={(e) => setForm({ ...form, honeypot: e.target.value })}
                style={{ position: "absolute", left: "-9999px", height: 0, width: 0 }}
                aria-hidden="true"
              />
              <div className="flex items-center justify-between pt-2">
                <div className="text-xs text-zinc-500 font-mono-data">
                  {form.message.length}/4000
                </div>
                <BrutalButton
                  data-testid={CONTACT_TESTIDS.submit}
                  type="submit"
                  disabled={disabled}
                >
                  <Send size={14} /> {submitting ? "Sending…" : "Send message"}
                </BrutalButton>
              </div>
            </form>
          )}
        </BentoCard>
      </section>
    </MarketingShell>
  );
}
